import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, Lock, LogOut, Moon, Settings, ShieldCheck, Sun, UserRound, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import { getAssociateData, isAuthenticated } from '../services/otpAuthService';
import { eDipendente, idPersonaChat, nomeDipendente } from '../utils/sezioniDipendente';
import { isRepresentativeAuthenticated } from '../services/representativeService';
import GlobalNotificationBell from './GlobalNotificationBell';
import { useGenerazionePersona } from '../hooks/useCambioPersonaAltraScheda';
import photoUrl from '../utils/photoUrl';
// Rimuoviamo gli enum, usiamo stringhe dirette

// Voce del menu: la pagina in cui ci si trova resta evidenziata
const classeVoce = ({ isActive }: { isActive: boolean }) =>
  `relative whitespace-nowrap px-2.5 xl:px-3.5 py-2 text-sm font-medium rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
    isActive
      ? 'text-primary-800 bg-primary-50 dark:text-primary-200 dark:bg-primary-900/40'
      : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
  }`;

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [scorrimento, setScorrimento] = useState(false);
  const profiloRef = useRef<HTMLDivElement>(null);

  // Debug: verifica il ruolo dell'utente
  React.useEffect(() => {
    if (user) {
      console.log('User roleId:', user.roleId?.name, 'Display:', user.roleId?.displayName);
    }
  }, [user]);

  // Appena si scorre la pagina il menu prende un'ombra leggera
  useEffect(() => {
    const aggiorna = () => setScorrimento(window.scrollY > 8);
    aggiorna();
    window.addEventListener('scroll', aggiorna, { passive: true });
    return () => window.removeEventListener('scroll', aggiorna);
  }, []);

  // Il menu del profilo si chiude cliccando fuori o con Esc
  useEffect(() => {
    if (!isProfileMenuOpen) return;
    const fuori = (e: MouseEvent) => {
      if (profiloRef.current && !profiloRef.current.contains(e.target as Node)) setIsProfileMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsProfileMenuOpen(false);
    };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', esc);
    };
  }, [isProfileMenuOpen]);

  // Determina se l'utente ha accesso all'area admin (RBAC)
  // Un utente ha accesso se ha ALMENO UN permesso dashboard.*
  const hasAdminAccess = hasAnyPermission([
    'dashboard.view',
    'dashboard.articles',
    'dashboard.galleries',
    'dashboard.users',
    'dashboard.certified',
    'dashboard.associates',
    'dashboard.membership_applications',
    'dashboard.certified_applications',
    'dashboard.tariffs',
    'dashboard.email_analytics',
    'dashboard.backup'
  ]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const percorsoAreaRiservata = isAuthenticated()
    ? '/area-riservata'
    : (isRepresentativeAuthenticated() ? '/area-associativa' : '/accesso-associati');

  // Dipendente dello studio: al posto di «Area Riservata» si vede chi è (studio e, sotto, la persona)
  // Cambio di persona in un'altra scheda (senza ricarica sulle pagine pubbliche): si ridisegna rileggendo la
  // sessione e la campanella si rimonta (o sparisce), chiudendo il socket della persona di prima
  const generazionePersona = useGenerazionePersona();
  const datiAssociato = isAuthenticated() ? getAssociateData() : null;
  const dipendente = eDipendente(datiAssociato) ? datiAssociato : null;

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-all duration-300 ${
        scorrimento
          ? 'bg-white/90 dark:bg-gray-950/90 border-gray-200/80 dark:border-gray-800/80 shadow-sm'
          : 'bg-white/80 dark:bg-black/80 border-gray-200/50 dark:border-gray-800/50'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex flex-shrink-0 items-center">
            <Link to="/" className="flex flex-shrink-0 items-center gap-3 group rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
              <img
                src="/images/logo-unasca.png"
                alt="UNASCA Logo"
                className="h-10 w-auto max-w-none flex-shrink-0 object-contain transform group-hover:scale-105 transition-transform"
                onError={(e) => {
                  e.currentTarget.src = '/favicon.ico';
                }}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5" aria-label="Menu principale">
            <NavLink to="/blog" className={classeVoce}>
              Blog
            </NavLink>
            <NavLink to="/gallery" className={classeVoce}>
              Galleria
            </NavLink>
            <NavLink to="/timeline" className={classeVoce}>
              Storia
            </NavLink>
            <NavLink
              to="/rete-certificata"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 xl:px-3.5 py-2 text-sm font-semibold rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  isActive
                    ? 'text-primary-800 bg-primary-50 dark:text-primary-200 dark:bg-primary-900/40'
                    : 'text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                }`
              }
            >
              <ShieldCheck className="hidden xl:block h-4 w-4" aria-hidden="true" />
              Rete Certificata
            </NavLink>
            <NavLink to="/convenzioni" className={classeVoce}>
              Convenzioni
            </NavLink>

            {user && user.membershipStatus === 'active' && (
              <NavLink to="/members" className={classeVoce}>
                Admin Area
              </NavLink>
            )}
            <NavLink to="/contact" className={classeVoce}>
              Contatti
            </NavLink>

            <NavLink
              to={percorsoAreaRiservata}
              className={(stato) => `${classeVoce(stato)} inline-flex items-center gap-1.5 ml-1 xl:ml-2`}
            >
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              {dipendente ? (
                <span className="flex min-w-0 flex-col leading-tight text-left" title={`${dipendente.nominativo} · ${nomeDipendente(dipendente)}`}>
                  <span className="max-w-[11rem] truncate">{dipendente.nominativo}</span>
                  <span className="max-w-[11rem] truncate text-xs font-normal text-gray-500 dark:text-gray-400">{nomeDipendente(dipendente)}</span>
                </span>
              ) : 'Area Riservata'}
            </NavLink>
            <Link
              to="/diventa-socio"
              className="ml-2 whitespace-nowrap px-3 xl:px-3.5 py-1.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-black"
            >
              Diventa Associato
            </Link>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Theme toggle - Nascosto su mobile (disponibile nel menu mobile) */}
            <button
              onClick={toggleTheme}
              className="hidden lg:flex h-9 w-9 items-center justify-center rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label={theme === 'light' ? 'Passa al tema scuro' : 'Passa al tema chiaro'}
            >
              {theme === 'light' ? <Moon className="h-5 w-5" aria-hidden="true" /> : <Sun className="h-5 w-5" aria-hidden="true" />}
            </button>

            {/* Notification Bell - Solo per associati autenticati - Desktop */}
            {isAuthenticated() && (
              <div className="hidden lg:block">
                {/* Una campanella per persona: sul browser condiviso il cambio titolare ↔ dipendente senza ricaricare
                    la rimonta (socket ed elenco della persona precedente non restano) */}
                <GlobalNotificationBell key={`${idPersonaChat(datiAssociato) || 'nessuno'}:${generazionePersona}`} />
              </div>
            )}

            {/* User menu - Solo Desktop */}
            {user ? (
              <div className="relative hidden lg:block" ref={profiloRef}>
                <button
                  onClick={toggleProfileMenu}
                  aria-expanded={isProfileMenuOpen}
                  aria-haspopup="menu"
                  className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <img
                    src={photoUrl(user.profile.foto) || user.profile.profilePicture || '/images/default-avatar.png'}
                    alt={user.profile.fullName || user.username}
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-white dark:ring-gray-900"
                    onError={(e) => {
                      // Evita loop infiniti controllando se stiamo già usando il fallback
                      if (e.currentTarget.src.includes('default-avatar.png')) {
                        // Se anche il default fallisce, usa un'icona SVG inline
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.querySelector('.avatar-fallback')) {
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.className = 'avatar-fallback w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-bold';
                          fallbackDiv.textContent = (user.profile.fullName || user.username).charAt(0).toUpperCase();
                          parent.appendChild(fallbackDiv);
                        }
                      } else {
                        e.currentTarget.src = '/images/default-avatar.png';
                      }
                    }}
                  />
                  <span className="hidden xl:block max-w-[10rem] truncate text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user.profile.fullName || user.username}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>

                {/* Profile dropdown */}
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-60 overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-black/5 dark:ring-white/10 z-50" role="menu">
                    <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{user.profile.fullName || user.username}</p>
                      {user.roleId?.displayName && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user.roleId.displayName}</p>
                      )}
                    </div>
                    <div className="p-1.5">
                      <Link
                        to="/profile"
                        role="menuitem"
                        className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        <UserRound className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        Il mio profilo
                      </Link>
                      {hasAdminAccess && (
                        <>
                          <Link
                            to="/admin"
                            role="menuitem"
                            className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <LayoutDashboard className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            Amministrazione
                          </Link>
                          <Link
                            to="/admin/settings"
                            role="menuitem"
                            className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <Settings className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            Impostazioni
                          </Link>
                        </>
                      )}
                    </div>
                    <div className="border-t border-gray-100 p-1.5 dark:border-gray-800">
                      <button
                        role="menuitem"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <LogOut className="h-4 w-4" aria-hidden="true" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
